


import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { IProjectFinancials } from "Dashboard/scenes/Projects/domain/IProjectFinancials";
import { hrsService } from "../../services/hrs.services";
import { ISalesUnitFinancials } from "../../domain/ISalesUnitFinancials";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { IGetFinancialsRequestParams } from "../../domain/IGetFinancialsRequestParams";
import { IConsumableExemptionFinancials } from "Dashboard/scenes/Projects/domain/IConsumableExemptionFinancials";
import { IConcreteBaseFinancials } from "Dashboard/scenes/Projects/domain/IConcreteBaseFinancials";
import { IGetSalesUnitFinancialsRequestParams } from "../../domain/IGetSalesUnitFinancialsRequestParams";

export const getBuildingFinancials = createAsyncThunk(
  "Buildings/getBuildingFinancials",
  async (data: IGetFinancialsRequestParams) => {
    try {
      return await hrsService.getBuildingFinancials(data);
    } catch (err) {
      setError(err);
    }
  }
);

export const getBuildingConsumableFinancials = createAsyncThunk(
  "Buildings/getBuildingConsumableFinancials",
  async (data: IGetFinancialsRequestParams) => {
    try {
      return await hrsService.getBuildingsConsumableFinancials(data);
    } catch (err) {
      setError(err);
    }
  }
);

export const getBuildingConcreteBaseFinancials = createAsyncThunk(
  "Buildings/getBuildingConcreteBaseFinancials",
  async (data: IGetFinancialsRequestParams) => {
    try {
      return await hrsService.getBuildingConcreteBaseFinancials(data);
    } catch (err) {
      setError(err);
    }
  }
);

export const getSalesUnitFinancials = createAsyncThunk(
  "SalesUnits/getSalesUnitFinancials",
  async (data: IGetSalesUnitFinancialsRequestParams) => {
    try {
      return await hrsService.getSalesUnitFinancials(data);
    } catch (err) {
      setError(err);
    }
  }
);

interface financialsState {
  generalFinancials: IProjectFinancials | undefined;
  consumableExemptionFinancials: IConsumableExemptionFinancials | undefined;
  concreteBaseOwnProductsFinancials: IConcreteBaseFinancials | undefined;
  financialsSalesUnit: ISalesUnitFinancials | undefined;
  selectedDateFrom: string | undefined;
  selectedDateTo: string | undefined;
  mealPriceSettingsPopover: IPopoverState;
  loading: boolean;
  error: any;
}

const initialState: financialsState = {
  generalFinancials: undefined,
  consumableExemptionFinancials: undefined,
  concreteBaseOwnProductsFinancials: undefined,
  financialsSalesUnit: undefined,
  selectedDateFrom: undefined,
  selectedDateTo: undefined,
  mealPriceSettingsPopover: { isOpen: false },
  loading: false,
  error: null,
};

export const financialsSlice = createSlice({
  name: "buildings-financials",
  initialState: initialState,
  reducers: {
    setSelectedDates: (
      state,
      action: PayloadAction<{
        dateFrom: string | undefined; dateTo: string | undefined
      }>
    ) => {
      const { dateFrom, dateTo } = action.payload;
      state.selectedDateFrom = dateFrom;
      state.selectedDateTo = dateTo;
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    updateAccountPayableNote: (
      state,
      action: PayloadAction<{ index: number; note: string }>
    ) => {
      if (state.generalFinancials) {
        state.generalFinancials.accountPayableFinancials.accountPayableEntries[
          action.payload.index].note = action.payload.note;
      }
    },
    updateAccountReceivableNote: (
      state,
      action: PayloadAction<{ index: number; note: string }>
    ) => {
      if (state.generalFinancials) {
        state.generalFinancials.accountReceivableFinancials.accountReceivableEntries[
          action.payload.index].note = action.payload.note;
      }
    },
    setMealPriceSettingsPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.mealPriceSettingsPopover = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getBuildingFinancials.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBuildingFinancials.fulfilled, (state, action) => {
      state.loading = false;
      state.generalFinancials = action.payload;
    });

    builder.addCase(getBuildingFinancials.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(getBuildingConsumableFinancials.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBuildingConsumableFinancials.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.consumableExemptionFinancials = action.payload;
      }
    });
    builder.addCase(getBuildingConsumableFinancials.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(getBuildingConcreteBaseFinancials.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBuildingConcreteBaseFinancials.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.concreteBaseOwnProductsFinancials = action.payload;
      }
    });
    builder.addCase(getBuildingConcreteBaseFinancials.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(getSalesUnitFinancials.fulfilled, (state, action) => {
      state.loading = false;
      state.financialsSalesUnit = action.payload;
    });
  },
});

export const {
  setSelectedDates,
  setError,
  updateAccountPayableNote,
  updateAccountReceivableNote,
  setMealPriceSettingsPopover
} = financialsSlice.actions;

export const selectState = (state: RootState) => state.hrs.financials;
export default financialsSlice.reducer;

